<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="emit('update:isVisible', $event)" size="medium" :zIndex="1150" isWithScroll>
    <template #header><h2 class="global-h2">Tag a business or location</h2></template>
    <template #content>
      <div class="location-picker">
        <div class="location-picker__search">
          <IconEmbedded name="magnifier_2-5" :size="22" color="currentColor" />
          <input v-model="query" class="global-text-input" placeholder="Search by business name or @handle" />
        </div>
        <Loader v-if="isLoading" />
        <div v-else-if="availableLocations.length" class="location-picker__list">
          <div v-for="location in availableLocations" :key="location.locationId" class="location-picker__row">
            <button type="button" class="location-picker__avatar-button" :aria-label="`Use ${location.name}`" @click="select(location)">
              <LocationAvatar :src="location.iconUrl" :name="location.name" :size="44" placeholderTone="gray" />
            </button>
            <div class="location-picker__copy">
              <div class="location-picker__title">
                <strong>{{ location.name }}</strong>
                <button type="button" class="location-picker__edit" aria-label="Edit location" @click.stop="edit(location)">
                  <IconEmbedded name="edit-pencil_2" :size="16" color="currentColor" />
                </button>
              </div>
              <div v-if="primaryHandle(location) || platformLinks(location).length" class="location-picker__meta">
                <small v-if="primaryHandle(location)" class="location-picker__handle">{{ primaryHandle(location) }}</small>
                <div v-if="platformLinks(location).length" class="location-picker__platforms">
                  <a
                    v-for="platform in platformLinks(location)"
                    :key="platform.name"
                    class="location-picker__platform"
                    :href="platform.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    :aria-label="platform.label"
                    @click.stop
                  >
                    <IconEmbedded :name="platform.name" :size="20" :color="platform.color" />
                  </a>
                </div>
              </div>
            </div>
            <SrpButton fill="outlined" color="teal" size="small" @click="select(location)">Set</SrpButton>
          </div>
        </div>
        <p v-else class="location-picker__empty">No accounts match your search.</p>
      </div>
    </template>
    <template #footer>
      <SrpButton fill="outlined" color="teal" @click="emit('create')">
        <template #icon><IconEmbedded name="plus_3" :size="18" /></template>
        Add New Account
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import Loader from "@components/Loader/Loader.vue";
import LocationAvatar from "./LocationAvatar.vue";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import type { IconName } from "@contracts/IconName";
import type { TaggableLocation } from "@contracts/socialLocations";
import { buildFacebookPageUrl, buildInstagramProfileUrl, buildTikTokProfileUrl } from "@logic/SocialFieldInputCleaner";

interface PlatformLink {
  name: IconName;
  label: string;
  color: string;
  href: string;
}

const props = withDefaults(defineProps<{ isVisible: boolean; customerId: string; excludeLocationIds?: string[] }>(), { excludeLocationIds: () => [] });
const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  (e: "select", location: TaggableLocation): void;
  (e: "create"): void;
  (e: "edit", locationId: string): void;
}>();

const query = ref("");
const locations = ref<TaggableLocation[]>([]);
const isLoading = ref(false);

watch(
  () => props.isVisible,
  async visible => {
    if (!visible) return;
    query.value = "";
    isLoading.value = true;
    try {
      locations.value = await SocialLocationsRepo.listTaggable(props.customerId);
    } finally {
      isLoading.value = false;
    }
  },
  { immediate: true }
);

const availableLocations = computed(() => {
  const excluded = new Set(props.excludeLocationIds);
  const normalized = query.value.trim().toLowerCase();
  return locations.value.filter(location => !excluded.has(location.locationId) && (!normalized || `${location.name} ${identitySummary(location)}`.toLowerCase().includes(normalized)));
});

function identitySummary(location: TaggableLocation): string {
  return [location.instagramHandle && `@${location.instagramHandle}`, location.facebookPageName, location.tikTokHandle && `TikTok @${location.tikTokHandle}`].filter(Boolean).join(" · ");
}

function primaryHandle(location: TaggableLocation): string {
  if (location.instagramHandle) return `@${location.instagramHandle}`;
  if (location.tikTokHandle) return `@${location.tikTokHandle}`;
  return "";
}

function platformLinks(location: TaggableLocation): PlatformLink[] {
  const platforms: PlatformLink[] = [];
  if (location.instagramHandle) {
    platforms.push({ name: "instagram", label: "Open Instagram profile", color: "#d13870", href: buildInstagramProfileUrl(location.instagramHandle) });
  }
  const facebookHref = buildFacebookPageUrl(location.facebookPageUrl, location.facebookPageId);
  if (facebookHref) {
    platforms.push({ name: "facebook", label: "Open Facebook page", color: "#1773ea", href: facebookHref });
  }
  if (location.tikTokHandle) {
    platforms.push({ name: "tiktok", label: "Open TikTok profile", color: "#000500", href: buildTikTokProfileUrl(location.tikTokHandle) });
  }
  return platforms;
}

function select(location: TaggableLocation) {
  emit("select", location);
  emit("update:isVisible", false);
}

function edit(location: TaggableLocation) {
  emit("edit", location.locationId);
}
</script>

<style scoped lang="scss">
.location-picker {
  display: grid;
  min-height: 180px;
}

.location-picker__search {
  position: relative;
  color: #667085;
  margin-bottom: 18px;

  > .embedded-icon {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 16px;
    transform: translateY(-50%);
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 54px;
    padding-left: 50px;
    border-color: #d0d5dd;
    border-radius: 8px;
    color: #202733;
  }
}

.location-picker__list {
  border-top: 1px solid #e4e7ec;
}

.location-picker__row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 12px;
  border-bottom: 1px solid #e4e7ec;
  background: white;
  text-align: left;
}

.location-picker__row:hover {
  background: #f8fbfb;
}

.location-picker__avatar-button {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  border-radius: 999px;
}

.location-picker__copy {
  flex: 1;
  display: grid;
  gap: 3px;
  min-width: 0;
  color: #202733;
}

.location-picker__title {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 16px;
    line-height: 1.25;
  }
}

.location-picker__edit {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #667085;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: #f2f4f7;
  }
}

.location-picker__handle {
  color: #667085;
  font-size: 14px;
  line-height: 1.3;
}

.location-picker__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.location-picker__platforms {
  display: flex;
  align-items: center;
  gap: 0;
}

.location-picker__platform {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 20px;
  border-radius: 4px;

  &:hover,
  &:focus-visible {
    background: #f2f4f7;
  }
}

.location-picker__empty {
  color: #667085;
  padding: 20px 0;
  text-align: center;
}

@media (max-width: 520px) {
  .location-picker__row {
    align-items: flex-start;
  }

  .location-picker__row > :last-child {
    margin-top: 4px;
  }
}
</style>
